import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { uid } from "uid/single";
import { useComposedRefs, useForceRerender, useMemoizedFn } from "@exonn/hooks";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum as TNS } from "src/lib/i18next/types.ts";
import { ButtonV2 } from "../ButtonV2/ButtonV2.tsx";
import DropIcon from "./assets/dropIcon.svg?react";
import UploadErrorIcon from "./assets/uploadError.svg?react";
import * as styles from "./DropZoneInputV2.style.ts";
import type { DragEvent, ChangeEvent } from "react";
import type { KeyboardEvent, ForwardedRef } from "react";

import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";

import {
  type Config,
  type InvalidMediaObject,
  type MediaAttributes,
  type MediaDotProperty,
  type MediaObject,
  type Options,
  type State,
  type ValidationErrors,
} from "./types.ts";
import type {
  AxiosInstance,
  AxiosProgressEvent,
  CancelTokenSource,
} from "axios";
import * as React from "react";

import { buildRuleHelpText } from "src/components/DropZoneInputV2/DropZonInputV2.helpers.ts";

import { InvalidMediaErrors } from "src/components/DropZoneInputV2/components/InvalidMediaErrors/InvalidMediaErrors.tsx";

import { Preview } from "src/components/DropZoneInputV2/components/Preview/Preview.tsx";
import { cloneDeep, set } from "lodash";
import {
  findObjectIndexByUuid,
  getFileTypeIsAllowed,
  getMediaPreview,
  mergeDeep,
} from "src/components/DropZoneInputV2/helpers.ts";
import { v4 as uuidv4 } from "uuid";
import {
  api,
  getCancelTokenSource,
  uploadFile,
} from "src/components/DropZoneInputV2/api.ts";

import { arrDeleteAt, arrInsertAt, arrReplaceAt, deepEq } from "@exonn/utils";
import { loadTranslations } from "src/lib/i18next/loadTranslations.ts";
import { InputLabel } from "src/components/InputLabel/InputLabel.tsx";

await loadTranslations(TNS.DropZoneInputComponentV2);

export type DropZoneV2Props = {
  name: string;
  initialValue?: Options["initialValue"];
  disableTemporaryUpload?: boolean;
  validationRules?: Partial<Config["validationRules"]>;
  validationErrors?: { [key: string]: Array<string> };
  multiple?: boolean;
  client?: AxiosInstance;
  maxItems?: number;
  invalidMediaValue?: Array<InvalidMediaObject>;
  onChangeInvalidMediaValue?: (value: Array<InvalidMediaObject>) => void;
  maxSizeForPreviewInBytes?: number;
  uploadDomain?: Config["uploadDomain"];
  withCredentials?: Config["withCredentials"];
  headers?: Config["headers"];
  fileTypeHelpText?: string;
  beforeUpload?: Config["beforeUpload"];
  afterUpload?: Config["afterUpload"];
  onChange?: (media: { [uuid: string]: MediaAttributes }) => void;
  onIsReadyToSubmitChange?: (isReadyToSubmit: boolean) => void;
  label?: string;
  isLoading?: boolean;
  helperText?: ReactNode;
  error?: string;
  previewType?: "chips" | "default";
  routePrefix?: string;

  value?: ReadonlyArray<MediaAttributes> | { [uuid: string]: MediaAttributes };
  // defaultValue?: readonly DropZoneValue[];
} & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "value" | "defaultValue" | "accept" | "onChange"
>;

const DropZoneInputComponent = (
  props: DropZoneV2Props,
  ref: ForwardedRef<HTMLInputElement>,
) => {
  const {
    label,
    helperText,
    required,
    error,
    disabled,
    isLoading = false,
    disableTemporaryUpload = false,
    previewType = "default",
    placeholder,
    multiple = false,
    value,
    onChange,
    initialValue,
    invalidMediaValue,
    client,
    validationRules: validationRulesProp,
    validationErrors: validationErrorsProp = {},
    maxItems = multiple ? undefined : 1,
    maxSizeForPreviewInBytes = 5242880,
    uploadDomain = "",
    withCredentials = true,
    headers,
    fileTypeHelpText,
    beforeUpload = () => {},
    routePrefix = "media-library-pro",
    afterUpload = () => {},
    onChangeInvalidMediaValue,
    ...rest
  } = props;

  const { t } = useTranslation(TNS.DropZoneInputComponentV2);
  const forceRerender = useForceRerender();

  const defaultMedia = value || initialValue;

  const state = useRef<State>({
    media: Array.isArray(defaultMedia)
      ? defaultMedia.map(createMedia)
      : Object.keys(defaultMedia || {}).map((key) =>
          createMedia(defaultMedia![key]),
        ),
    invalidMedia: invalidMediaValue || [],
    validationErrors: validationErrorsProp || {},
    draggedItemId: null,
  });

  const changeState = useMemoizedFn((callback: (state: State) => void) => {
    callback(state.current);
    onChangeInvalidMediaValue?.(state.current.invalidMedia);
    onChange?.(
      state.current.media.reduce(
        (acc, object) => {
          return {
            ...acc,
            [object.attributes.uuid]: object.attributes,
          };
        },
        {} as { [uuid: string]: MediaAttributes },
      ),
    );
    forceRerender();
  });

  useEffect(() => {
    if (value) {
      const nextState = Array.isArray(value)
        ? value.map(createMedia)
        : Object.keys(value || {}).map((key) => createMedia(value![key]));

      const nextStateObj = nextState.reduce((acc, object) => {
        return {
          ...acc,
          [object.attributes.uuid]: object.attributes,
        };
      }, {});

      const prevStateObj = state.current.media.reduce((acc, object) => {
        return {
          ...acc,
          [object.attributes.uuid]: object.attributes,
        };
      }, {});

      const orderHasChanged =
        Object.keys(nextStateObj).join("") !==
        Object.keys(prevStateObj).join("");

      const deepEqual = deepEq(nextStateObj, prevStateObj);
      if (!deepEqual || orderHasChanged) {
        changeState((state) => {
          state.media = nextState;
        });
      }
    }
  }, [value, changeState]);

  useEffect(() => {
    if (invalidMediaValue) {
      const deepEqual = deepEq(state.current.invalidMedia, invalidMediaValue);
      if (!deepEqual) {
        changeState((state) => {
          state.invalidMedia = invalidMediaValue || [];
        });
      }
    }
  }, [invalidMediaValue, changeState]);

  const validationRules = mergeDeep(
    { accept: [], minSizeInKB: 0, maxSizeInKB: 0 },
    validationRulesProp || {},
  );

  const accept = validationRules?.accept || [];

  const removeMediaInternal = (uuid: string) => {
    clearInvalidMedia();
    const index = findObjectIndexByUuid(state.current.media, uuid);

    if (index !== -1) {
      // If an upload is in progress for this object, cancel it.
      cancelUpload(uuid);

      changeState((state) => {
        state.media.splice(index, 1);
      });
    }
  };

  const removeMedia = (object: MediaObject) => {
    removeMediaInternal(object.attributes.uuid);
  };
  const setProperty = (uuid: string, key: MediaDotProperty, value: any) => {
    const index = findObjectIndexByUuid(state.current.media, uuid);
    if (index !== -1) {
      changeState((state) => {
        const newObject = {
          ...state.media[index],
        };
        set(newObject, key, value);
        state.media[index] = newObject;
      });
    }
  };

  const cancelUpload = (uuid: string) => {
    const index = findObjectIndexByUuid(state.current.media, uuid);

    if (index !== -1) {
      const object = state.current.media[index];
      if (
        !object.upload.hasFinishedUploading &&
        object.upload.cancelTokenSource
      ) {
        object.upload.cancelTokenSource.cancel();
      }

      setProperty(uuid, "upload", {
        uploadProgress: 0,
        hasFinishedUploading: true,
        hadErrorWhileUploading: false,
        errorMessage: null,
        cancelTokenSource: null,
      });
    }
  };

  const getFileValidationErrors = async (
    file: File,
  ): Promise<Array<string>> => {
    const validationErrors: Array<string> = [];

    if (validationRules) {
      if (!getFileTypeIsAllowed(file.type, validationRules.accept)) {
        validationErrors.push(
          `${t("fileTypeNotAllowed")} ${validationRules.accept.join(", ")}`,
        );
      }

      if (
        validationRules.maxSizeInKB &&
        file.size / 1024 > validationRules.maxSizeInKB
      ) {
        validationErrors.push(
          `${t("tooLarge")} ${validationRules.maxSizeInKB}KB`,
        );
      }

      if (
        validationRules.minSizeInKB &&
        file.size / 1024 < validationRules.minSizeInKB
      ) {
        validationErrors.push(
          `${t("tooSmall")} ${validationRules.minSizeInKB}KB`,
        );
      }
    }

    try {
      await Promise.resolve(beforeUpload(file, { axiosInstance: api }));
    } catch (error) {
      if (error) {
        validationErrors.push((error as any).message || error);
      }
    }

    return validationErrors;
  };

  const addFile = async (file: File) => {
    clearInvalidMedia();
    const validationErrors = await getFileValidationErrors(file);

    if (validationErrors.length) {
      addInvalidMedia(file, validationErrors);

      return;
    }

    const uuid = uuidv4();
    const cancelTokenSource = getCancelTokenSource();

    const newMediaObject: MediaObject = {
      attributes: {
        uuid,
        name: file.name,
        order: state.current.media.length,
        custom_properties: {},
        preview_url: null,
        tempFile: file,
        original_url: null,
        extension: file.name.split(".").pop(),
        size: file.size,
      },
      client_preview: null,
      clientValidationErrors: [],
      upload: {
        uploadProgress: 0,
        hasFinishedUploading: false,
        hadErrorWhileUploading: false,
        errorMessage: null,
        cancelTokenSource,
      },
    };

    setClientPreview(uuid, file);
    changeState((state) => {
      state.media.push(newMediaObject);
    });

    if (disableTemporaryUpload) {
      return;
    }

    const { success, error } = await upload(
      uuid,
      file,
      cancelTokenSource,
      client,
    );

    if (!success) {
      removeMediaInternal(uuid);
      addInvalidMedia(file, [error!]);
    }
  };

  const setClientPreview = async (uuid: string, file: File) => {
    const client_preview = await getMediaPreview(
      file,
      maxSizeForPreviewInBytes,
    );

    setProperty(uuid, "client_preview", client_preview);
  };

  const clearInvalidMedia = () => {
    changeState((state) => {
      state.invalidMedia = [];
    });
  };

  const addInvalidMedia = async (
    file: File,
    validationErrors: Array<string>,
  ) => {
    const client_preview = await getMediaPreview(
      file,
      maxSizeForPreviewInBytes,
    );

    changeState((state) => {
      state.invalidMedia = [
        ...state.invalidMedia,
        {
          attributes: {
            name: file.name,
          },
          client_preview,
          errors: validationErrors,
        },
      ];
    });
  };

  const setClientValidationErrors = (uuid: string, errors: Array<string>) => {
    const index = findObjectIndexByUuid(state.current.media, uuid);

    if (index !== -1) {
      changeState((state) => {
        state.media = arrReplaceAt(state.media, index, {
          ...state.media[index],
          clientValidationErrors: errors,
        });
      });
    }
  };

  const replaceMediaInternal = async (
    uuid: string,
    file: File,
    client?: AxiosInstance,
  ) => {
    clearInvalidMedia();
    setClientValidationErrors(uuid, []);

    const validationErrors = await getFileValidationErrors(file);

    if (validationErrors.length) {
      return addInvalidMedia(file, validationErrors);
    }

    cancelUpload(uuid);

    const index = findObjectIndexByUuid(state.current.media, uuid);
    const oldObject = cloneDeep(state.current.media[index]);

    if (index === -1) {
      return;
    }

    const newUuid = uuidv4();
    const cancelTokenSource = getCancelTokenSource();

    // Because the uuid changes, we need to update the (backend) validation errors to use this new uuid.
    const newValidationErrors = Object.entries(
      state.current.validationErrors,
    ).reduce((validationErrors, [key, value]) => {
      validationErrors[key.replace(uuid, newUuid)] = value;

      return validationErrors;
    }, {} as ValidationErrors);

    changeState((state) => {
      state.media[index] = {
        ...state.media[index],
        attributes: {
          ...state.media[index].attributes,
          uuid: newUuid,
          preview_url: null,
          tempFile: file,
          size: file.size,
          extension: file.name.split(".").pop(),
          name: file.name,
        },
        client_preview: null,
        upload: {
          ...state.media[index].upload,
          cancelTokenSource,
        },
      };
      state.validationErrors = newValidationErrors;
    });

    setClientPreview(newUuid, file);

    if (disableTemporaryUpload) {
      return;
    }

    const { success, error } = await upload(
      newUuid,
      file,
      cancelTokenSource,
      client,
    );

    if (!success) {
      // Reset the object to the old object, but don't overwrite the custom properties, in case those were edited during upload
      const index = findObjectIndexByUuid(state.current.media, newUuid);

      if (index === -1) {
        return;
      }
      addInvalidMedia(file, [error!]);

      changeState((state) => {
        state.media[index] = {
          ...state.media[index],
          attributes: {
            ...oldObject.attributes,
            order: state.media[index].attributes.order,
            custom_properties: state.media[index].attributes.custom_properties,
          },
          clientValidationErrors: oldObject.clientValidationErrors,
          client_preview: oldObject.client_preview,
          upload: {
            ...oldObject.upload,
            errorMessage: state.media[index].upload.errorMessage,
          },
        };
      });
    }
  };

  const upload = async (
    uuid: string,
    file: File,
    cancelTokenSource: CancelTokenSource,
    client?: AxiosInstance,
  ): Promise<{ success: boolean; error?: string }> => {
    return new Promise((resolve) => {
      setProperty(uuid, "upload.errorMessage", null);
      setProperty(uuid, "upload.hasFinishedUploading", false);

      uploadFile({
        routePrefix: routePrefix,
        file,
        uuid,
        client,
        cancelTokenSource,
        uploadDomain: uploadDomain,
        withCredentials: withCredentials,
        headers: headers || {},
        onUploadProgress: (progress: AxiosProgressEvent) => {
          if (progress.total) {
            setProperty(
              uuid,
              "upload.uploadProgress",
              (progress.loaded / progress.total) * 100,
            );
          }
        },
      })
        .then((res) => {
          setProperty(uuid, "attributes.preview_url", res.data.preview_url);
          setProperty(uuid, "attributes.original_url", res.data.original_url);
          setProperty(uuid, "attributes.size", res.data.size);
          setProperty(uuid, "attributes.extension", res.data.extension);

          setProperty(uuid, "upload", {
            uploadProgress: 100,
            hasFinishedUploading: true,
            hadErrorWhileUploading: false,
            errorMessage: null,
          });

          afterUpload({ success: true, uuid: uuid });

          resolve({ success: true });
        })
        .catch((error) => {
          if (error.__CANCEL__) {
            return resolve({ success: true });
          }

          const errorMessage = error.message || t("somethingWentWrong");

          setProperty(uuid, "upload", {
            uploadProgress: 0,
            hasFinishedUploading: true,
            hadErrorWhileUploading: true,
            errorMessage: errorMessage + " - " + t("tryAgain"),
          });

          afterUpload({ success: false, uuid });

          resolve({ success: false, error: errorMessage });
        });
    });
  };

  const replaceMedia = (object: MediaObject, file: File) =>
    replaceMediaInternal(object.attributes.uuid, file, client);

  const [isDrag, setIsDrag] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const composedRef = useComposedRefs(inputRef, ref);

  const inputId = uid();
  const hasUploadError = state.current.invalidMedia.length > 0;

  const getPlaceholderText = () => {
    if (hasUploadError) {
      return t("uploadError");
    }
    if (placeholder) return placeholder;
    return t("dragOrChoose");
  };

  const handleFileList = (fileList: FileList) => {
    const availableSlots = maxItems
      ? Math.min(maxItems - state.current.media.length, fileList.length)
      : fileList.length;
    for (let i = 0; i < availableSlots; i++) {
      addFile(fileList[i]);
    }
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDrag(true);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDrag(false);
    handleFileList(e.dataTransfer.files);
  };

  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDrag(false);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.target.files && handleFileList(e.target.files);
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleKeyDown = (ev: KeyboardEvent<HTMLDivElement>) => {
    if (ev.code === "Enter" || ev.code === "Space") {
      ev.preventDefault();
      handleClick();
    }
  };

  const handleMove = (draggedItem: MediaObject, moveToIndex: number) => {
    changeState((state) => {
      const previousIndex = state.media.findIndex(
        (media) => media.attributes.uuid === draggedItem.attributes.uuid,
      );
      if (previousIndex !== -1) {
        state.media = arrInsertAt(
          arrDeleteAt(state.media, previousIndex),
          moveToIndex,
          state.media[previousIndex],
        );

        state.media = state.media.map((object, index) => ({
          ...object,
          attributes: {
            ...object.attributes,
            order: index,
          },
        }));
      }
    });
  };

  const handleChangeDraggedItemId = (id: string | null) => {
    changeState((state) => {
      state.draggedItemId = id;
    });
  };

  const maxItemsUploaded = !!maxItems && maxItems <= state.current.media.length;

  return (
    <Skeleton isLoading={isLoading}>
      <div css={styles.rootContainer}>
        {label && (
          <InputLabel
            label={label}
            required={required}
            helperText={helperText}
          />
        )}

        <div
          tabIndex={0}
          css={styles.rootDropZoneStyle({ error, disabled })}
          onDrop={handleDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onKeyDown={handleKeyDown}
        >
          {!maxItemsUploaded && (
            <div css={styles.rootInput({ isDrag, error: hasUploadError })}>
              <div className="progress" />

              {hasUploadError ? (
                <UploadErrorIcon css={styles.errorIcon} />
              ) : (
                <DropIcon css={styles.dropIcon} />
              )}

              <label htmlFor={inputId}>
                <span>{getPlaceholderText()}</span>

                <input
                  ref={composedRef}
                  id={inputId}
                  hidden
                  disabled={disabled}
                  type="file"
                  multiple={multiple}
                  onChange={handleFileChange}
                  accept={accept.length > 0 ? accept.join(",") : undefined}
                  {...rest}
                />
              </label>

              <ButtonV2
                tabIndex={-1}
                skin="info"
                size="sm"
                variant="filled"
                css={{ flexShrink: 0 }}
                onClick={handleClick}
                disabled={disabled || maxItemsUploaded}
              >
                {t("chooseBtn")}
              </ButtonV2>

              <div className="dragOver">{t("dropHere")}</div>
            </div>
          )}

          {state.current.invalidMedia.length > 0 && (
            <InvalidMediaErrors
              invalidMedia={state.current.invalidMedia}
              media={state.current.media}
              maxItems={maxItems}
              onClear={() => clearInvalidMedia()}
            />
          )}

          {!maxItemsUploaded && (
            <p css={styles.validParameters}>
              {buildRuleHelpText({
                validationRules,
                maxItems,
                fileTypeHelpText,
                t,
              })}
            </p>
          )}

          <Preview
            draggedItemId={state.current.draggedItemId}
            onChangeDraggedItemId={handleChangeDraggedItemId}
            onMove={handleMove}
            invalidMedia={state.current.invalidMedia}
            media={state.current.media}
            maxItems={maxItems}
            onRemove={removeMedia}
            onReplace={replaceMedia}
            previewType={previewType}
            validationRules={validationRules}
          />
        </div>

        {error && !disabled && <p css={styles.error}>{error}</p>}
      </div>
    </Skeleton>
  );
};

export const DropZoneInputV2 = forwardRef(DropZoneInputComponent);

const createMedia = (attributes: Partial<MediaAttributes>): MediaObject => {
  if (!attributes.uuid) {
    throw new Error("A media object requires a uuid");
  }

  return {
    attributes: {
      uuid: "",
      order: 0,
      name: "",
      preview_url: null,
      original_url: null,
      ...attributes,
      custom_properties:
        typeof attributes.custom_properties === "string"
          ? JSON.parse(attributes.custom_properties)
          : attributes.custom_properties || {},
    },
    client_preview: null,
    clientValidationErrors: [],
    upload: {
      uploadProgress: 100,
      hasFinishedUploading: true,
      hadErrorWhileUploading: false,
      errorMessage: null,
      cancelTokenSource: null,
    },
  };
};
